import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SalonService from "../../services/SalonService";
import moment from "moment";
import "./AppointmentDetails.css";
import GlobalLoader from "../loadingIndicator/GlobalLoader";
import { toast } from "react-toastify";
import {
    FaCalendarAlt, FaClock, FaCut, FaCheckCircle,
    FaStore, FaWallet, FaChevronLeft
} from "react-icons/fa";

export default function AppointmentDetails() {
    const { id: paramId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Use ID from URL params first, fallback to location state
    const apntId = paramId || location.state?.apntId;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (apntId) {
            fetchDetails();
        } else {
            setLoading(false);
            toast.error("Appointment ID not found");
        }
    }, [apntId]);

    const fetchDetails = async () => {
        try {
            const res = await SalonService.getAppointmentById(apntId);
            setData(res.data);
        } catch (e) {
            console.error("Failed to load appointment", e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <GlobalLoader />;

    if (!data) return (
        <div className="empty-state-container">
            <p>No appointment details found.</p>
            <button className="btn btn-primary" onClick={() => navigate('/viewBookings')}>
                Back to Appointments
            </button>
        </div>
    );

    const getStatusClass = (status) => {
        return status?.toLowerCase() || 'default';
    };

    return (
        <div className="details-page-wrapper">
            <div className="details-content-container py-4">
                <div className="premium-details-card shadow-lg">
                    <div className="salon-brand-section">
                        <div className="brand-icon-box">
                            <FaStore />
                        </div>
                        <h2 className="salon-name-display">{data.salonName}</h2>
                        <div className={`status-badge-premium ${getStatusClass(data.apntStatus)}`}>
                            {data.apntStatus}
                        </div>
                    </div>

                    <div className="details-grid-premium">
                        <div className="detail-row-premium">
                            <FaCalendarAlt className="detail-icon-premium" />
                            <div className="detail-info-premium">
                                <span className="detail-label-premium">Date</span>
                                <span className="detail-value-premium">{moment(data.apntDate).format("DD MMM, YYYY")}</span>
                            </div>
                        </div>

                        <div className="detail-row-premium">
                            <FaClock className="detail-icon-premium" />
                            <div className="detail-info-premium">
                                <span className="detail-label-premium">Time</span>
                                <span className="detail-value-premium">{data.apntTime}</span>
                            </div>
                        </div>

                        <div className="detail-row-premium full-width-row">
                            <FaCut className="detail-icon-premium" />
                            <div className="detail-info-premium">
                                <span className="detail-label-premium">Services</span>
                                <span className="detail-value-premium">{data.serviceNames}</span>
                            </div>
                        </div>

                        {data.advanceAmount > 0 && (
                            <div className="detail-row-premium">
                                <FaWallet className="detail-icon-premium" />
                                <div className="detail-info-premium">
                                    <span className="detail-label-premium">Advance Paid</span>
                                    <span className="detail-value-premium highlight">₹{data.advanceAmount}</span>
                                </div>
                            </div>
                        )}

                        {data.verificationNumber && data.verificationNumber !== "null" && (
                            <div className="detail-row-premium">
                                <FaCheckCircle className="detail-icon-premium" />
                                <div className="detail-info-premium">
                                    <span className="detail-label-premium">Verification Code</span>
                                    <span className="detail-value-premium code">{data.verificationNumber}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="details-footer-premium">
                        <p className="footer-note-premium">
                            Please show this screen or provide the verification code at the salon.
                        </p>
                    </div>
                </div>

                <div className="action-buttons-group">
                    <button
                        className="premium-action-btn primary"
                        onClick={() => navigate(`/booking`)}
                    >
                        <FaCalendarAlt className="me-2" /> Book Another Appointment
                    </button>

                    <button
                        className="premium-action-btn secondary"
                        onClick={() => navigate(`/viewBookings`)}
                    >
                        <FaChevronLeft className="me-2" /> View All Bookings
                    </button>
                </div>
            </div>
        </div>
    );
}
